/**
 * Shopify Checkout API
 * Functions for checkout operations using the Shopify Storefront API
 */

import { CartStorage } from "@/libs/cart-storage";
import { getCart } from "./shopify-cart";
import { getAffiliateParams } from "@/AffiliateTracker/affiliate-tracker";

/**
 * Create a checkout from the current cart
 * This function gets the current cart and returns its checkout URL
 * @returns The checkout data including the checkout URL
 */
export const createCheckout = async () => {
  try {
    // Get the current cart
    const cartId = CartStorage.getCartToken();
    const cart = await getCart(cartId || undefined);

    if (!cart) {
      throw new Error("No cart available to create checkout");
    }

    // Get affiliate params from session
    const affiliateData = await getAffiliateParams();

    // Store checkout information
    if (cart.id) {
      CartStorage.saveCheckoutId(cart.id);
    }

    // Return checkout data
    return {
      id: cart.id,
      webUrl: cart.checkoutUrl,
      totalPrice: cart.cost?.totalAmount?.amount,
      currencyCode: cart.cost?.totalAmount?.currencyCode,
      lineItems:
        cart.lines?.edges?.map((edge: any) => {
          const node = edge.node;
          const merchandise = node.merchandise;
          return {
            id: node.id,
            quantity: node.quantity,
            title: merchandise.product.title,
            variant: {
              id: merchandise.id,
              title: merchandise.title,
              price: merchandise.price.amount,
              product: {
                id: merchandise.product.id,
                title: merchandise.product.title,
                handle: merchandise.product.handle,
              },
            },
          };
        }) || [],
      // Include affiliate data if available
      attributes: affiliateData || {},
    };
  } catch (error) {
    console.error("Error creating checkout:", error);
    throw error;
  }
};

/**
 * Get the checkout URL for the current cart
 * @returns The checkout URL
 */
export const getCheckoutUrl = async (): Promise<string> => {
  try {
    const checkout = await createCheckout();
    return checkout.webUrl || "";
  } catch (error) {
    console.error("Error getting checkout URL:", error);
    return "";
  }
};

/**
 * Complete a checkout (redirect to Shopify checkout)
 * This is typically handled by redirecting the user to the checkout URL
 * @returns The checkout URL to redirect to
 */
export const completeCheckout = async (): Promise<string> => {
  return getCheckoutUrl();
};
